"use client"

import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Code2, 
  Palette, 
  Zap, 
  GitBranch, 
  Smartphone, 
  Shield, 
  Sparkles, 
  Database, 
  Globe,
  ArrowRight,
  CheckCircle2
} from "lucide-react"

interface Feature {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  benefits: string[]
}

const features: Feature[] = [
  {
    icon: Sparkles,
    title: "AI-Powered Generation",
    description: "Transform natural language prompts into production-ready event platforms with advanced AI models.",
    benefits: ["Natural language processing", "Smart code generation", "Context-aware suggestions"]
  },
  {
    icon: Palette,
    title: "Visual Drag & Drop",
    description: "Build stunning event interfaces with our intuitive drag-and-drop editor and real-time preview.",
    benefits: ["No-code builder", "Real-time preview", "Template library"]
  },
  {
    icon: Code2,
    title: "Professional Code Editor",
    description: "Full-featured Monaco editor with syntax highlighting, IntelliSense, and debugging tools.",
    benefits: ["Auto-completion", "Git integration", "Multi-cursor editing"]
  },
  {
    icon: GitBranch,
    title: "Version Control",
    description: "Seamless Git integration with GitHub and automated deployment workflows for your events.",
    benefits: ["Branch management", "Pull requests", "Auto-deploy"]
  },
  {
    icon: Zap,
    title: "One-Click Deploy",
    description: "Deploy to Vercel, Netlify, or any platform with a single click and automatic CI/CD pipelines.",
    benefits: ["Zero-config deploy", "Auto-scaling", "Custom domains"]
  },
  {
    icon: Smartphone,
    title: "Responsive Design",
    description: "Generate mobile-first, responsive event platforms that work perfectly on all devices.",
    benefits: ["Mobile-first", "Cross-browser", "PWA support"]
  },
  {
    icon: Database,
    title: "Database Integration",
    description: "Connect to popular databases with auto-generated schemas and secure API endpoints.",
    benefits: ["Auto-schema generation", "Real-time sync", "Data migration"]
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "Built-in security best practices, authentication, and compliance-ready features.",
    benefits: ["OAuth 2.0", "GDPR compliant", "Encrypted data"]
  },
  {
    icon: Globe,
    title: "Global CDN",
    description: "Lightning-fast performance with global content delivery and edge optimization for your events.",
    benefits: ["Edge caching", "DDoS protection", "99.9% uptime"]
  }
]

export function FeaturesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  return (
    <section id="features" className="relative py-24 overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
          animationDuration: '8s'
        }}
      />
      <div className="absolute bottom-20 right-10 w-72 h-72 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
          animationDuration: '6s',
          animationDelay: '2s'
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center animate-in fade-in slide-in-from-bottom duration-700">
          <Badge 
            variant="secondary" 
            className="mb-6 px-4 py-2 text-sm glass-card border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Sparkles className="mr-2 h-4 w-4" style={{ color: `rgb(var(--theme-primary))` }} />
            Powerful Features
          </Badge>
          
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-5xl mb-6">
            Everything You Need to Build{" "}
            <span 
              className="relative inline-block"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Modern Events
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C50 2.5 150 2.5 199 5.5"
                  stroke={`rgb(var(--theme-accent))`}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
          
          <p className="mx-auto mt-6 max-w-3xl text-lg text-muted-foreground leading-relaxed">
            From AI-powered event generation to professional deployment tools, 
            Swaraah provides the complete event management experience.
          </p>
        </div>

        {/* Features Grid */}
        <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => {
            const IconComponent = feature.icon
            const isHovered = hoveredIndex === index
            
            return (
              <Card 
                key={index} 
                className="group relative overflow-hidden glass-card border backdrop-blur-sm transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                style={{
                  borderColor: isHovered 
                    ? `rgba(var(--theme-primary), 0.5)` 
                    : `rgba(var(--border), 0.5)`
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Hover Gradient Background */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  }}
                />
                
                <CardContent className="relative p-8">
                  {/* Animated Icon Container */}
                  <div className="mb-6 relative">
                    <div 
                      className="inline-flex h-14 w-14 items-center justify-center rounded-xl shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                      }}
                    >
                      <IconComponent className="h-7 w-7 text-white transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    
                    {/* Pulse Effect on Hover */}
                    {isHovered && (
                      <div 
                        className="absolute inset-0 rounded-xl animate-ping"
                        style={{
                          background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                          opacity: 0.2
                        }}
                      />
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="mb-3 text-xl font-bold text-foreground transition-colors duration-300 group-hover:text-foreground">
                    {feature.title}
                  </h3>
                  
                  <p className="text-muted-foreground leading-relaxed mb-6 transition-colors duration-300">
                    {feature.description}
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-2 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-40 transition-all duration-500 overflow-hidden">
                    {feature.benefits.map((benefit, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-start gap-2"
                        style={{
                          animationDelay: `${idx * 100}ms`
                        }}
                      >
                        <CheckCircle2 
                          className="h-4 w-4 mt-0.5 flex-shrink-0" 
                          style={{ color: `rgb(var(--theme-primary))` }}
                        />
                        <span className="text-sm text-muted-foreground">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* Learn More Button (appears on hover) */}
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <button 
                      className="flex items-center gap-2 text-sm font-medium transition-colors duration-300"
                      style={{ color: `rgb(var(--theme-primary))` }}
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>

                  {/* Bottom Border Animation */}
                  <div 
                    className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-700"
                    style={{
                      background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                    }}
                  />
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-300">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 glass-card rounded-2xl p-8 border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.03)`
            }}
          >
            <div className="flex-1 text-left">
              <h3 className="text-xl font-bold text-foreground mb-2">
                Ready to build something amazing?
              </h3>
              <p className="text-muted-foreground">
                Start creating your perfect event platform today with AI assistance.
              </p>
            </div>
            <button
              className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            >
              Get Started Free
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
